import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';
import { Button } from '@client/components/ui/button';
import { UserPlus } from 'lucide-react';
import { fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest } from '@client/model/advisorHierarchyModel';
import {
  selectSessionAdvisor,
  selectAdvisorHierarchyState,
  selectSupervisorAdvisorRelationships,
} from '@client/model/selectors';
import { TeamTable } from '@/components/advisor-hierarchy/TeamTable';
import { MembershipTable } from '@/components/advisor-hierarchy/MembershipTable';
import { PendingInvitationsSection } from '@/components/advisor-hierarchy/PendingInvitationsSection';
import { AcceptInvitationDialog } from '@/components/advisor-hierarchy/AcceptInvitationDialog';
import { InviteAdvisorDialog } from '@/components/advisor-hierarchy/InviteAdvisorDialog';
import { useAdvisorHierarchyInvitation } from '@/components/advisor-hierarchy/useAdvisorHierarchyInvitation';
import { useAdvisorHierarchyDelete } from '@/components/advisor-hierarchy/useAdvisorHierarchyDelete';
import { useAdvisorHierarchyResend } from '@/components/advisor-hierarchy/useAdvisorHierarchyResend';
import { useAdvisorHierarchyLeave } from '@/components/advisor-hierarchy/useAdvisorHierarchyLeave';
import { useAdvisorHierarchyAcceptDecline } from '@/components/advisor-hierarchy/useAdvisorHierarchyAcceptDecline';
import { selectIsImpersonating } from '@/model/selectors';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@client/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@client/components/ui/select';

type TeamView = 'owner' | 'member' | 'invitations';

export function AdvisorHierarchyTeamPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const isImpersonating = useSelector(selectIsImpersonating);

  // Get session advisor for refresh
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const { relationshipsFetchStatus } = useSelector(selectAdvisorHierarchyState);

  // Get supervisor relationships from Redux store
  const supervisorRelationships = useSelector(selectSupervisorAdvisorRelationships);

  // Invitation hook - handles new invitations
  const {
    relationships,
    isMutating: isInviting,
    currentMutation,
    dialogOpen,
    emailError,
    openDialog,
    closeDialog,
    handleEmailChange,
    validateEmail,
    handleSendInvitation,
    handleDirectAssign,
  } = useAdvisorHierarchyInvitation();

  // Delete hook - handles relationship deletion
  const {
    deleteDialogOpen,
    relationshipToDelete,
    isDeleting,
    handleDelete,
    confirmDelete,
    cancelDelete,
  } = useAdvisorHierarchyDelete();

  // Leave hook - handles leaving a team (disabled during impersonation)
  const {
    leaveDialogOpen,
    relationshipToLeave,
    isLeaving,
    isDisabled: isLeaveDisabled,
    handleLeave,
    confirmLeave,
    cancelLeave,
  } = useAdvisorHierarchyLeave({ adminModeActive: isImpersonating });

  // Resend hook - handles resending invitations
  const { handleResend, isResending } = useAdvisorHierarchyResend();

  // Accept/Decline hook - handles accepting or declining pending supervisor invitations (disabled during impersonation)
  const {
    pendingInvitations,
    acceptDialogOpen,
    invitationToAccept,
    declineDialogOpen,
    invitationToDecline,
    isAccepting,
    isDeclining,
    isProcessing,
    isDisabled: isAcceptDeclineDisabled,
    handleAccept,
    confirmAccept,
    cancelAccept,
    handleDecline,
    confirmDecline,
    cancelDecline,
  } = useAdvisorHierarchyAcceptDecline({ adminModeActive: isImpersonating });

  const hasPendingInvitations = pendingInvitations && pendingInvitations.length > 0;
  const hasSubordinates = relationships && relationships.length > 0;
  const hasSupervisors = supervisorRelationships && supervisorRelationships.length > 0;

  const advisorIdForRefresh = sessionAdvisor?.advisorId;

  const handleRefresh = () => {
    if (advisorIdForRefresh) {
      dispatch(
        fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest({
          advisorId: advisorIdForRefresh,
        }),
      );
    }
  };

  const isRefreshing = relationshipsFetchStatus === 'loading';

  // Determine default view based on priority:
  // 1. Invitations (if any)
  // 2. Owner (if has subordinates)
  // 3. Member (if has supervisors)
  // 4. Owner (fallback)
  const getDefaultView = (): TeamView => {
    if (hasPendingInvitations) return 'invitations';
    if (hasSubordinates) return 'owner';
    if (hasSupervisors) return 'member';
    return 'owner';
  };

  // Get view from query params, or use computed default
  const viewParam = searchParams.get('view') as TeamView | null;
  const view: TeamView = viewParam || getDefaultView();

  const setView = (newView: TeamView) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('view', newView);
    setSearchParams(newParams, { replace: true });
  };

  return (
    <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <DashboardPageHeader title="Team" />

      {/* View Toggle and Invite Button */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">View</span>
          <Select
            value={view}
            onValueChange={(value: TeamView) => setView(value)}
          >
            <SelectTrigger className="w-[140px] text-gray-700">
              <SelectValue placeholder="Select view" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="owner">Owner</SelectItem>
              <SelectItem value="member">Member</SelectItem>
              <SelectItem value="invitations">
                Invitations{hasPendingInvitations && ` (${pendingInvitations.length})`}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {view === 'owner' && (
          <Button onClick={openDialog}>
            <UserPlus className="mr-2 h-4 w-4" />
            Invite Advisor
          </Button>
        )}
      </div>

      {/* View Description */}
      <p className="mb-4 text-sm text-gray-700">
        {view === 'owner' &&
          (isImpersonating
            ? "Managing this advisor's team members. You can invite new members and manage their access."
            : 'Advisors who are part of your organization. You can invite new members and manage their access.')}
        {view === 'member' &&
          (isImpersonating
            ? 'Organizations this advisor has granted access to.'
            : 'Organizations you have granted access to. These owners can view and manage your clients based on the permissions you have provided.')}
        {view === 'invitations' &&
          (isImpersonating
            ? 'Pending invitations for this advisor. Accept/decline actions are disabled in admin mode.'
            : 'Review invitations from other organizations who want you to join their team.')}
      </p>

      {view === 'owner' && (
        <TeamTable
          relationships={relationships}
          onDelete={handleDelete}
          onResend={handleResend}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          isAdminMode={isImpersonating}
        />
      )}

      {view === 'member' && (
        <MembershipTable
          supervisorRelationships={supervisorRelationships}
          onLeave={handleLeave}
          isLeaveDisabled={isLeaveDisabled}
        />
      )}

      {view === 'invitations' && (
        <PendingInvitationsSection
          invitations={pendingInvitations}
          onAccept={handleAccept}
          onDecline={handleDecline}
          isProcessing={isProcessing}
          decliningInvitationId={isDeclining ? invitationToDecline?.invitationId : null}
          isDisabled={isAcceptDeclineDisabled}
        />
      )}

      <InviteAdvisorDialog
        open={dialogOpen}
        onClose={closeDialog}
        onInvite={handleSendInvitation}
        onDirectAssign={handleDirectAssign}
        validateEmail={validateEmail}
        emailError={emailError}
        onEmailChange={handleEmailChange}
        isMutating={isInviting}
        currentMutation={currentMutation}
        isAdminMode={isImpersonating}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Team Member</AlertDialogTitle>
            <AlertDialogDescription>
              {relationshipToDelete?.relationshipStatus === 'active'
                ? `Are you sure you want to remove ${relationshipToDelete?.advisorFirstName && relationshipToDelete?.advisorLastName ? `${relationshipToDelete.advisorFirstName} ${relationshipToDelete.advisorLastName}` : relationshipToDelete?.advisorEmail} from your team? This action cannot be undone.`
                : `Are you sure you want to delete the invitation sent to ${relationshipToDelete?.advisorEmail}? This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDelete}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-700 text-white hover:bg-red-700"
              onClick={confirmDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Leave Team Confirmation Dialog */}
      <AlertDialog open={leaveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave Team</AlertDialogTitle>
            <AlertDialogDescription>
              {`Are you sure you want to leave ${relationshipToLeave?.advisorFirstName && relationshipToLeave?.advisorLastName ? `${relationshipToLeave.advisorFirstName} ${relationshipToLeave.advisorLastName}'s` : relationshipToLeave?.advisorEmail ? `${relationshipToLeave.advisorEmail}'s` : 'this'} team? They will no longer have access to your clients.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelLeave}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-700 text-white hover:bg-red-700"
              onClick={confirmLeave}
            >
              Leave Team
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Accept Invitation Dialog (with consent form) */}
      <AcceptInvitationDialog
        open={acceptDialogOpen}
        invitation={invitationToAccept}
        onAccept={confirmAccept}
        onDecline={cancelAccept}
        isSubmitting={isAccepting}
      />

      {/* Decline Invitation Confirmation Dialog */}
      <AlertDialog open={declineDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Decline Invitation</AlertDialogTitle>
            <AlertDialogDescription>
              {`Are you sure you want to decline the invitation from ${invitationToDecline?.supervisorFirstName && invitationToDecline?.supervisorLastName ? `${invitationToDecline.supervisorFirstName} ${invitationToDecline.supervisorLastName}` : invitationToDecline?.supervisorEmail}? The sender will be notified.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={cancelDecline}
              disabled={isDeclining}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-700 text-white hover:bg-red-700 disabled:opacity-50"
              onClick={confirmDecline}
              disabled={isDeclining}
            >
              {isDeclining ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Declining...
                </span>
              ) : (
                'Decline Invitation'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
